"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.nullToUndefined = nullToUndefined;
exports.emptyToNull = emptyToNull;
exports.employeeModelToAPISchema = employeeModelToAPISchema;
exports.companyModelToAPISchema = companyModelToAPISchema;
exports.venueModelToAPISchema = venueModelToAPISchema;
exports.departmentModelToAPISchema = departmentModelToAPISchema;
exports.positionModelToAPISchema = positionModelToAPISchema;
exports.positionAPISchemaToModel = positionAPISchemaToModel;
exports.timeEntryModelToAPISchema = timeEntryModelToAPISchema;
exports.timeEntryAPISchemaToModel = timeEntryAPISchemaToModel;
exports.timeEntryPublishedInfoModelToAPISchema = timeEntryPublishedInfoModelToAPISchema;
exports.timeEntryPublishedInfoAPISchemaToModel = timeEntryPublishedInfoAPISchemaToModel;
exports.timeLogEventModelToAPISchema = timeLogEventModelToAPISchema;
exports.documentToAPISchema = documentToAPISchema;
const model_1 = require("../model");
const colors_1 = require("../colors");
// turns null values to undefined to represent an empty value in typeorm and the database
function nullToUndefined(obj) {
    return Object.keys(obj).reduce((acc, key) => {
        acc[key] = obj[key] === null ? undefined : obj[key];
        return acc;
    }, {}); // eslint-disable-line @typescript-eslint/no-explicit-any
}
// turns empty values ['undefined' and empty string ''] to null to be returned as JSON in API
function emptyToNull(obj) {
    return Object.keys(obj).reduce((acc, key) => {
        acc[key] = obj[key] === undefined || obj[key] === "" ? null : obj[key];
        return acc;
    }, {}); // eslint-disable-line @typescript-eslint/no-explicit-any
}
function employeeModelToAPISchema(company, employee, includeRestricted) {
    var _a, _b, _c, _d;
    return emptyToNull(includeRestricted
        ? {
            id: employee.id,
            firstName: employee.firstName,
            lastName: employee.lastName,
            callName: employee.callName,
            address: employee.address,
            city: employee.city,
            postalCode: employee.postalCode,
            email: employee.email,
            phone: employee.phone,
            phone2: employee.phone2,
            birthday: employee.birthday,
            birthName: employee.birthName,
            birthCity: employee.birthCity,
            birthCountry: employee.birthCountry,
            nationality: employee.nationality,
            gender: employee.gender,
            taxId: employee.taxId,
            socialSecurityNumber: employee.socialSecurityNumber,
            notes: employee.notes,
            staffNumber: (_a = employee.staffNumber) === null || _a === void 0 ? void 0 : _a.toString(),
            avatar: employee.avatar,
            positions: ((_b = employee.positions) === null || _b === void 0 ? void 0 : _b.map((position) => {
                const color = company.getPositionColor(position);
                return {
                    id: position.id,
                    name: position.name,
                    color: (color && (0, colors_1.getHexColor)(color)) || color,
                };
            })) || [],
            timeLogPin: employee.timePin,
            status: employee.status,
        }
        : {
            id: employee.id,
            firstName: employee.firstName,
            lastName: employee.lastName,
            callName: employee.callName,
            staffNumber: (_c = employee.staffNumber) === null || _c === void 0 ? void 0 : _c.toString(),
            avatar: employee.avatar,
            positions: ((_d = employee.positions) === null || _d === void 0 ? void 0 : _d.map((position) => {
                const color = company.getPositionColor(position);
                return {
                    id: position.id,
                    name: position.name,
                    color: (color && (0, colors_1.getHexColor)(color)) || color,
                };
            })) || [],
            timeLogPin: employee.timePin,
            status: employee.status,
        });
}
function companyModelToAPISchema(company) {
    return emptyToNull({
        id: company.id,
        name: company.name,
        venues: company.venues.map(venueModelToAPISchema),
    });
}
function venueModelToAPISchema(venue) {
    const { id, name, venueNumber, holidays, enabledHolidays, departments } = venue;
    return emptyToNull({
        id,
        name,
        companyId: venue.companyId,
        venueNumber,
        holidays: holidays || enabledHolidays,
        departments: (departments === null || departments === void 0 ? void 0 : departments.map(departmentModelToAPISchema)) || [],
    });
}
function departmentModelToAPISchema(department) {
    var _a, _b;
    return emptyToNull({
        id: department.id,
        name: department.name,
        venueId: department.venueId,
        color: (0, colors_1.getHexColor)(department.color),
        positions: (_a = department.positions) === null || _a === void 0 ? void 0 : _a.map(positionModelToAPISchema),
        timeScheduleEmployeeOrder: (_b = department.rosterOrder) === null || _b === void 0 ? void 0 : _b.map((id) => Number(id)),
    });
}
function positionModelToAPISchema(position) {
    return emptyToNull({
        id: position.id,
        name: position.name,
        distinctName: position.distinctName,
        color: (0, colors_1.getHexColor)(position.color),
        departmentId: position.departmentId,
    });
}
function positionAPISchemaToModel(position) {
    return new model_1.Position(nullToUndefined({
        id: position.id,
        name: position.name,
        color: position.color,
    }));
}
function timeEntryModelToAPISchema(company, entry) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const position = (_a = company.getPosition(entry.positionId)) === null || _a === void 0 ? void 0 : _a.position;
    const type = entry.type === model_1.TimeEntryType.Work ? "work" : entry.type;
    return {
        id: entry.id,
        date: entry.date,
        status: getStatus(entry),
        startPlanned: ((_b = entry.startPlanned) === null || _b === void 0 ? void 0 : _b.toISOString()) || null,
        endPlanned: ((_c = entry.endPlanned) === null || _c === void 0 ? void 0 : _c.toISOString()) || null,
        startLogged: ((_d = entry.startLogged) === null || _d === void 0 ? void 0 : _d.toISOString()) || null,
        endLogged: ((_e = entry.endLogged) === null || _e === void 0 ? void 0 : _e.toISOString()) || null,
        startFinal: ((_f = entry.startFinal) === null || _f === void 0 ? void 0 : _f.toISOString()) || null,
        endFinal: ((_g = entry.endFinal) === null || _g === void 0 ? void 0 : _g.toISOString()) || null,
        currentBreakStart: ((_h = entry.startBreak) === null || _h === void 0 ? void 0 : _h.toISOString()) || null,
        employeeId: entry.employeeId || null,
        type,
        breakPlanned: typeof entry.breakPlanned === "number" ? entry.breakPlanned : 0,
        breakLogged: typeof entry.breakLogged === "number" ? entry.breakLogged : 0,
        breakFinal: typeof entry.break === "number" ? entry.break : 0,
        result: entry.result || undefined,
        position: (position &&
            positionModelToAPISchema({
                ...position,
                color: company.getPositionColor(position),
            })) ||
            null,
        mealsBreakfast: entry.mealsBreakfast,
        published: timeEntryPublishedInfoModelToAPISchema(company, entry.published),
        seen: timeEntryPublishedInfoModelToAPISchema(company, entry.seen),
        mealsLunch: entry.mealsLunch,
        mealsDinner: entry.mealsDinner,
        comment: entry.comment,
    };
}
function timeEntryAPISchemaToModel(entry) {
    var _a;
    const timeEntryBase = nullToUndefined({
        id: entry.id,
        date: entry.date,
        startPlanned: entry.startPlanned ? new Date(entry.startPlanned) : null,
        endPlanned: entry.endPlanned ? new Date(entry.endPlanned) : null,
        startLogged: entry.startLogged ? new Date(entry.startLogged) : null,
        endLogged: entry.endLogged ? new Date(entry.endLogged) : null,
        startFinal: entry.startFinal ? new Date(entry.startFinal) : null,
        endFinal: entry.endFinal ? new Date(entry.endFinal) : null,
        startBreak: entry.currentBreakStart ? new Date(entry.currentBreakStart) : null,
        employeeId: entry.employeeId || null,
        type: entry.type,
        breakPlanned: entry.breakPlanned,
        breakLogged: entry.breakLogged,
        break: entry.breakFinal,
        result: entry.result,
        positionId: ((_a = entry.position) === null || _a === void 0 ? void 0 : _a.id) || null,
        mealsBreakfast: entry.mealsBreakfast,
        published: entry.published ? timeEntryPublishedInfoAPISchemaToModel(entry.published) : null,
        seen: entry.seen ? timeEntryPublishedInfoAPISchemaToModel(entry.seen) : null,
        mealsLunch: entry.mealsLunch,
        mealsDinner: entry.mealsDinner,
        comment: entry.comment,
        position: entry.position ? positionAPISchemaToModel(entry.position) : null,
    });
    return new model_1.TimeEntry(timeEntryBase);
}
function timeEntryPublishedInfoModelToAPISchema(company, info) {
    var _a, _b, _c, _d;
    if (!info) {
        return info;
    }
    const position = info.positionId && ((_a = company.getPosition(info.positionId)) === null || _a === void 0 ? void 0 : _a.position);
    return {
        ...info,
        position: position ? positionModelToAPISchema(position) : null,
        time: info.time.toISOString(),
        deleted: ((_b = info.deleted) === null || _b === void 0 ? void 0 : _b.toISOString()) || null,
        startPlanned: ((_c = info.startPlanned) === null || _c === void 0 ? void 0 : _c.toISOString()) || null,
        endPlanned: ((_d = info.endPlanned) === null || _d === void 0 ? void 0 : _d.toISOString()) || null,
    };
}
function timeEntryPublishedInfoAPISchemaToModel(info) {
    var _a;
    return {
        time: new Date(info.time),
        deleted: info.deleted ? new Date(info.deleted) : null,
        employeeId: info.employeeId,
        date: info.date,
        positionId: ((_a = info.position) === null || _a === void 0 ? void 0 : _a.id) || null,
        startPlanned: info.startPlanned ? new Date(info.startPlanned) : null,
        endPlanned: info.endPlanned ? new Date(info.endPlanned) : null,
        comment: info.comment,
    };
}
function timeLogEventModelToAPISchema({ id, action, deviceId, employeeId, positionId, timeEntryId, time, status, rejectedReason, location, image, }) {
    return {
        id,
        action,
        deviceId,
        employeeId,
        positionId,
        timeEntryId,
        time: time.toISOString(),
        status,
        rejectedReason,
        location,
        image,
    };
}
function documentToAPISchema(document) {
    return {
        id: document.id,
        uuid: document.uuid,
        companyId: document.companyId,
        employeeId: document.employeeId,
        createdById: document.createdById,
        name: document.name,
        comment: document.comment,
        date: document.date,
        type: document.type,
        tags: document.tags.map((tag) => tag.name),
        url: document.url,
    };
}
function getStatus(entry) {
    if (entry.type !== model_1.TimeEntryType.Work) {
        return "completed";
    }
    if (entry.endFinal) {
        return "completed";
    }
    if (entry.startBreak) {
        return "paused";
    }
    if (entry.startFinal) {
        return "ongoing";
    }
    return "scheduled";
}
//# sourceMappingURL=transform.js.map